/**
 * BigTextBlock Component
 *
 * Displays a large KPI card with current value, previous period comparison,
 * delta, and percentage change with visual indicators.
 *
 * Performance: Uses ChartDataContext for cached data - no individual API calls
 */

import PropTypes from 'prop-types';
import { useChartData } from '../../contexts/ChartDataContext';
import { getDeltaIndicator } from '../../utils/formatters';
import './BigTextBlock.css';

const BigTextBlock = ({ chartId }) => {
  // Get chart data from cache (loaded via batch request in template)
  const { getChartData: getCachedChartData } = useChartData();
  const cachedData = getCachedChartData(chartId);

  const { data, loading, error } = cachedData;

  if (loading) {
    return (
      <div className="big-text-block loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="big-text-block error">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="big-text-block no-data">
        <p>No data available</p>
      </div>
    );
  }

  // Extract values from data
  const {
    chart_name,
    metric_name,
    formatted_current,
    formatted_delta,
    formatted_previous,
    delta,
    delta_percentage,
    is_positive_good,
    current_period_label,
    previous_period_label,
    previous_value,
    dashboard_date_range_label,
  } = data;

  // Get delta indicator (arrow and color)
  const indicator = getDeltaIndicator(delta, is_positive_good);

  // Build title with period label
  const titleWithPeriod = current_period_label
    ? `${chart_name || metric_name} (${current_period_label})`
    : chart_name || metric_name;

  return (
    <div className="big-text-block">
      <div className="big-text-block-header">
        <h3 className="chart-title">{titleWithPeriod}</h3>
        {dashboard_date_range_label && (
          <div className="date-range-label">{dashboard_date_range_label}</div>
        )}
      </div>

      <div className="big-text-block-body">
        <div className="current-value">
          {formatted_current}
        </div>

        {/* Previous period info */}
        {previous_value !== null && previous_value !== undefined && (
          <div className="previous-period-info">
            {previous_period_label && (
              <span className="previous-period-label">Prev period: {previous_period_label}</span>
            )}
            {formatted_previous && (
              <span className="previous-period-value">Prev value: {formatted_previous}</span>
            )}
          </div>
        )}

        {/* Delta/Change comparison */}
        {delta !== null && delta !== undefined && (
          <div className={`comparison ${indicator.colorClass}`}>
            <span className="delta-label">Change:</span>
            <span className="delta-arrow">{indicator.arrow}</span>
            {delta_percentage !== null && delta_percentage !== undefined && (
              <span className="delta-percentage">
                {delta_percentage >= 0 ? '+' : ''}{delta_percentage.toFixed(1)}%
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

BigTextBlock.propTypes = {
  chartId: PropTypes.string.isRequired,
};

export default BigTextBlock;
